<script lang="ts">
  // The "we can't read this community's member list" prompt, as a chip in the shell's
  // community header. That page is the single source for membership status, plan, LTV,
  // renewal dates and departures, so without it the whole membership surface renders
  // empty and the win-back queue silently includes people who already left — which
  // looks like a product bug rather than a permission we were never given. This says
  // so, and says exactly how to fix it.
  //
  // IT USED TO BE A FULL-WIDTH STRIP above the panel, and the move into the header is
  // what let the copy shrink: the strip had to name the community ("Membership and
  // revenue for X is locked") because it floated above a page that never said which
  // community it was about. The header names it two elements to the left, so the chip
  // states the fact and the popover carries the instructions that used to sit on an
  // expanding shelf.
  //
  // Sits beside TrialBanner and follows the same community selection, but deliberately
  // reads DIFFERENT from it: the trial chip is a deadline (primary blue, live
  // countdown), this is an unlocked-capability nudge (warning ochre, no clock). Two
  // urgent-looking blue chips would make neither mean anything.
  //
  // The `unknown` state renders nothing on purpose — a community no deep pass has
  // checked hasn't refused us anything, and accusing it would be a lie we'd have to
  // retract on the next tick.
  import { activeCommunity } from '../community.svelte';
  // `GET /v1/scraper-account` — shape-identical to the legacy `/admin/roster-account`
  // (same three keys, same nullability), so only the path and the error envelope
  // changed. Account-scoped and gated `admin` rather than `active`, which is what lets
  // this prompt render for an owner who has not paid yet.
  import { fetchScraperAccount, type ScraperAccount } from '../session';
  import { dismissable } from './dismissable';

  /**
   * `dismissible` — whether "Dismiss for now" is offered, and whether a previous
   * dismissal hides this. TRUE in the header, where the chip is ambient and an owner
   * who has read it should be able to quiet it. FALSE where the chip is the ONLY
   * explanation for something visibly missing (the pipeline board's empty columns):
   * there, hiding it would leave a column that says "we can't see this" with no way
   * to act on it.
   *
   * `showLabel` — whether the chip states the fact as well as the action. The header
   * has room for both; a 14rem pipeline column does not, and the sentence above the
   * chip there has already said it.
   */
  let {
    dismissible = true,
    showLabel = true,
  }: { dismissible?: boolean; showLabel?: boolean } = $props();

  const active = $derived(activeCommunity());
  const rosterState = $derived(active?.roster_state ?? 'unknown');
  const show = $derived(rosterState === 'ask' || rosterState === 'escalate');

  // The account to promote. One global fact, so it's fetched once and only when a
  // prompt is actually going to render — most owners never see this.
  let account = $state<ScraperAccount | null>(null);
  $effect(() => {
    if (!show || account) return;
    void fetchScraperAccount()
      .then((a) => (account = a))
      .catch(() => {}); // advisory prompt: the email fallback below still renders
  });

  // Dismissal is per community AND per state, so escalating from "ask" to "escalate"
  // re-surfaces the chip: the ask has genuinely changed and the owner needs to see the
  // new one. sessionStorage, not local — this is worth raising again next visit.
  const key = $derived(active ? `sg:roster-dismissed:${active.id}:${rosterState}` : '');
  let dismissedKey = $state<string | null>(null);
  $effect(() => {
    dismissedKey = key ? sessionStorage.getItem(key) : null;
  });
  const visible = $derived(show && !!active && (!dismissible || dismissedKey !== '1'));

  function dismiss(): void {
    if (key) sessionStorage.setItem(key, '1');
    dismissedKey = '1';
    open = false;
  }

  let open = $state(false);

  /**
   * WHY THE PANEL IS `fixed` AND NOT `absolute`.
   *
   * This chip is mounted in two places, and one of them clips. The pipeline board's
   * empty columns render it inside a column body that is `overflow-y-auto` and a board
   * wrapper that is `overflow-x-auto overflow-y-hidden` — an absolutely-positioned
   * panel cannot escape either, so the instructions were cut off and what survived
   * disappeared under the sticky sidebar. That is a CLIPPING bug, not a z-index one:
   * raising `z-30` higher fixes nothing, because the pixels are never painted.
   *
   * A fixed element is positioned against the viewport and is not clipped by ancestor
   * overflow, so the panel escapes both scrollers. It relies on no ancestor
   * establishing a containing block for fixed descendants (`transform`, `filter`,
   * `will-change`, `contain`) — checked across the shell, the pipelines page, the board
   * and the card tile; the only `backdrop-blur` is the mobile header, which is a
   * sibling of the content column rather than an ancestor of it.
   *
   * The panel stays a DOM child of the trigger's wrapper, so `dismissable`'s
   * `contains()` test still treats clicks inside it as inside.
   */
  let triggerEl = $state<HTMLElement | null>(null);
  let pos = $state<{ top: number; left: number; width: number } | null>(null);

  /** Matches the panel's old `w-[min(30rem,…)]`, in px, against the real root font
   *  size — this app sets `html { font-size: 106.25% }`, so a hardcoded 16 would make
   *  the panel narrower than it was and re-wrap the instructions. */
  function place(): void {
    const el = triggerEl;
    if (!el) return;
    const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const r = el.getBoundingClientRect();
    const width = Math.min(30 * rem, window.innerWidth - 16);
    // Right-aligned to the chip, then clamped into the viewport — in a narrow pipeline
    // column the panel is far wider than its trigger and would otherwise hang off the
    // left edge, which is what put it under the sidebar.
    const left = Math.min(Math.max(8, r.right - width), window.innerWidth - width - 8);
    pos = { top: r.bottom + 8, left, width };
  }

  // Re-place while open. `capture: true` on scroll is load-bearing: the column that
  // holds this chip scrolls INTERNALLY, and a scroll on an inner element does not
  // bubble to window without it — the panel would sit still while the chip moved away.
  $effect(() => {
    if (!open) return;
    place();
    const onMove = (): void => place();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  });

  // Their own members page, where the role change happens.
  const membersUrl = $derived(
    active ? `https://www.skool.com/${active.slug}/-/members?t=admin` : 'https://www.skool.com',
  );
  // What we already hold, for the escalate copy.
  const held = $derived(active?.roster_role ?? null);
</script>

{#if visible && active}
  <!-- No `relative` here any more: the panel is fixed, so this wrapper anchors nothing.
       It stays only as the node `dismissable` tests clicks against. -->
  <div use:dismissable={{ isOpen: () => open, close: () => (open = false) }}>
    <button
      bind:this={triggerEl}
      onclick={() => (open = !open)}
      aria-expanded={open}
      class="inline-flex items-center gap-1.5 rounded-full border border-warning/35 bg-warning/12 px-2.5 py-1
             text-[0.7rem] font-medium text-warning transition-colors hover:bg-warning/20"
    >
      <!-- lock-open: this is a capability to unlock, not an error to fear -->
      <svg class="h-3.5 w-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 9.9-1" />
      </svg>
      <!-- Says what we NEED and why, not which internal surface is empty. "Membership
           & revenue locked" named a symptom the reader has to map back to a cause;
           this names the cause and the fix in one line. The two states differ by the
           role Skool actually requires — moderator first, admin once moderator has
           proved insufficient — so the chip always asks for the right thing.

           Hidden below `sm`, and wherever the caller has already said it, so a phone
           header and a narrow column stay one line; the icon and the action word
           still carry it. -->
      {#if showLabel}
        <span class="hidden sm:inline">
          {rosterState === 'escalate'
            ? 'For full features we need an admin role'
            : 'For full features we need a moderator role'}
        </span>
      {/if}
      <span class="font-semibold underline underline-offset-2">
        {open ? 'Hide' : rosterState === 'escalate' ? 'What now' : 'Unlock it'}
      </span>
    </button>

    {#if open}
      <!-- The instructions that used to live on an expanding shelf under the strip.
           Fixed and measured — see `place()` above for why it cannot be absolute. -->
      <div
        class="fixed z-50 rounded-box border hair bg-base-100 p-4 text-xs leading-relaxed shadow-xl"
        style="top:{pos?.top ?? 0}px; left:{pos?.left ?? 0}px; width:{pos?.width ?? 0}px;
               visibility:{pos ? 'visible' : 'hidden'};"
      >
        <p class="text-base-content/80">
          {#if rosterState === 'escalate'}
            Thanks for making us {held === 'moderator' ? 'a moderator' : `an ${held}`} — but Skool
            gates the member list and its billing tabs behind a higher role than the one we hold.
            Raising us to <span class="font-semibold text-base-content">Admin</span> on this
            community is what opens it.
          {:else}
            Make our account <span class="font-semibold text-base-content">Moderator</span> on this
            community. If the data still doesn’t appear within a day, Skool needs
            <span class="font-semibold text-base-content">Admin</span> instead — this chip will say so.
          {/if}
        </p>

        <ol class="mt-2.5 space-y-1 text-base-content/80">
          <li>
            1. Open
            <a href={membersUrl} target="_blank" rel="noopener noreferrer" class="text-primary underline underline-offset-2 hover:opacity-80">
              <span class="redact">{active.name}</span> → Members
            </a>
            on Skool.
          </li>
          <li>
            2. Find
            {#if account?.handle}
              <span class="redact font-mono font-semibold text-base-content">@{account.handle}</span>{#if account.name}<span class="opacity-80"> ({account.name})</span>{/if}
            {:else}
              our account
            {/if}
            {#if account?.email}
              <span class="redact opacity-80">— invited as <span class="font-mono">{account.email}</span></span>
            {/if}.
          </li>
          <li>
            3. Change their role to
            <span class="font-semibold text-base-content">{rosterState === 'escalate' ? 'Admin' : 'Moderator'}</span>.
          </li>
        </ol>

        <p class="mt-2.5 text-base-content/65">
          We read the member list to fill in who’s still a member, what they pay, when they renew
          and who has left. We never post, message anyone, or change anything in your community.
        </p>

        <!-- Dismissal moved in here from a separate × on the strip. The chip is one
             control now; burying "not now" one click deep keeps the header to it. -->
        {#if dismissible}
        <div class="mt-3 flex justify-end border-t hair pt-2.5">
          <button onclick={dismiss} class="text-[0.7rem] text-base-content/60 underline underline-offset-2 hover:text-base-content">
            Dismiss for now
          </button>
        </div>
        {/if}
      </div>
    {/if}
  </div>
{/if}
